import { useCallback, useMemo, useState } from 'react'
import {
  formatSpartacusPriorDateRange,
  formatSpartacusUkDateRange,
  getSpartacusPriorPeriodTooltip,
  isSpartacusDateRangeWithinLimit,
  resolveSpartacusDateRange,
} from 'src/utils/spartacusDateRange'
import {
  buildSpartacusQueryParams,
  buildSpartacusStatsQueryParams,
  createInitialSpartacusPageState,
  setSpartacusDateRange,
  setSpartacusFilters,
  setSpartacusPagination,
  setSpartacusSort,
} from 'src/utils/spartacusPageState'

export function useSpartacusPageState() {
  const [state, setState] = useState(() => createInitialSpartacusPageState())

  const ukDateRangeLabel = useMemo(
    () => formatSpartacusUkDateRange(state),
    [state.fromDate, state.toDate],
  )

  const ukPriorDateRangeLabel = useMemo(
    () => formatSpartacusPriorDateRange(state),
    [state.fromDate, state.toDate],
  )

  const priorPeriodTooltip = useMemo(
    () => getSpartacusPriorPeriodTooltip(state),
    [state.fromDate, state.toDate],
  )

  const statsQueryParams = useMemo(
    () => buildSpartacusStatsQueryParams(state),
    [state.fromDate, state.toDate, state.filters],
  )

  const venuesQueryParams = useMemo(
    () => buildSpartacusQueryParams(state),
    [
      state.fromDate,
      state.toDate,
      state.filters,
      state.page,
      state.pageSize,
      state.sortBy,
      state.sortDir,
    ],
  )

  const setDateRange = useCallback((fromDate, toDate) => {
    const resolved = resolveSpartacusDateRange({ fromDate, toDate })

    if (!isSpartacusDateRangeWithinLimit(resolved.fromDate, resolved.toDate)) {
      throw new Error('Date range is too large')
    }

    setState(prev => setSpartacusDateRange(prev, resolved))
  }, [])

  const updateFilters = useCallback(filters => {
    setState(prev => setSpartacusFilters(prev, filters))
  }, [])

  const updatePagination = useCallback(({ page, pageSize }) => {
    setState(prev => setSpartacusPagination(prev, { page, pageSize }))
  }, [])

  const updateSort = useCallback(({ sortBy, sortDir }) => {
    setState(prev => setSpartacusSort(prev, { sortBy, sortDir }))
  }, [])

  return {
    state,
    ukDateRangeLabel,
    ukPriorDateRangeLabel,
    priorPeriodTooltip,
    statsQueryParams,
    venuesQueryParams,
    setDateRange,
    updateFilters,
    updatePagination,
    updateSort,
  }
}
